import { useFirebaseUser } from "./useFirebaseUser";
import {
  useApiQuery,
  type Api,
  type QueryResult,
  apiClient,
  getApiQueryData,
  refetchApiQuery,
  setApiQueryBody,
  clearApiQuery,
} from "../../../services/api";
import { getFirebaseToken } from "../../../services/firebase";
import i18n from "../../../config/i18n";
import { normalizeLanguage } from "../../app/utils/langUtils";
import { isUserOnboarded } from "../utils/isUserOnboarded";
import { useSignupInFlight } from "../../auth/stores/signupInFlightStore";

export type CurrentUser =
  Api["get"]["/webapp/private/users/me"]["output"]["body"];

function getCurrentUserQueryInput(): {
  query: {
    language: CurrentUser["language"];
  };
} {
  return {
    query: {
      language: normalizeLanguage(i18n.resolvedLanguage ?? navigator.language),
    },
  };
}

/**
 * The user the app shows, from the `/users/me` query.
 *
 * A failed refetch keeps the last known user unless the server refused the
 * account (a 403). React Query keeps the cached body when a background
 * refetch fails, but the query result reports `error` without it. Offline, a
 * refetch fails on the network or on the Firebase token refresh, and returning
 * `null` there made every page render as logged out (the header shows the login
 * icon, Discovery shows the anonymous feed) while the session was still valid.
 * A 401 keeps the user too: in the webapp it is a stale token, not a signed-out
 * session (`services/api.ts` `onUnauthorized`).
 */
function readCurrentUser(
  userCache: QueryResult<Api["get"]["/webapp/private/users/me"]>
): CurrentUser | null {
  switch (userCache.state) {
    case "idle":
    case "pending":
      return null;
    case "success":
      return userCache.body;
    case "error": {
      if (userCache.status === 403) return null;
      return getApiQueryData(
        "/webapp/private/users/me",
        getCurrentUserQueryInput()
      );
    }
    default:
      throw new Error(userCache satisfies never);
  }
}

export function useCurrentUser(): {
  currentUser: CurrentUser | null;
  isLoading: boolean;
  isOnboarded: boolean;
  // A Firebase session exists — the SERVER-INDEPENDENT proof of "logged in".
  // `currentUser === null` alone does NOT mean logged out: it also covers the
  // /users/me fetch being in flight or failed (API down), so guards must key
  // "logged out" on `!isAuthenticated`, never on `currentUser === null` —
  // otherwise an API hiccup bounces an authenticated user to the login wall.
  isAuthenticated: boolean;
} {
  const { isAuthenticatedFirebase, isLoadingFirebaseUser } = useFirebaseUser();
  const signupInFlight = useSignupInFlight();
  const shouldFetch = isAuthenticatedFirebase && !signupInFlight;
  const userCache = useApiQuery(
    "/webapp/private/users/me",
    shouldFetch
      ? {
          ...getCurrentUserQueryInput(),
          durable: true,
          keepPreviousData: true,
        }
      : false
  );

  const currentUser = readCurrentUser(userCache);
  const isLoading =
    isLoadingFirebaseUser ||
    signupInFlight ||
    (shouldFetch && userCache.state === "pending" && userCache.isFetching);
  const isOnboarded = isUserOnboarded(currentUser);

  return {
    currentUser,
    isLoading,
    isOnboarded,
    isAuthenticated: isAuthenticatedFirebase,
  };
}

// Non-hook utilities for use outside React (analytics, optimistic updates)

export function getCurrentUserData(): CurrentUser | null {
  return getApiQueryData(
    "/webapp/private/users/me",
    getCurrentUserQueryInput()
  );
}

export function replaceCurrentUserData(data: CurrentUser | null) {
  if (data === null) {
    clearApiQuery("/webapp/private/users/me", getCurrentUserQueryInput());
  } else {
    setApiQueryBody(
      "/webapp/private/users/me",
      getCurrentUserQueryInput(),
      () => data
    );
  }
}

/**
 * Force-refresh the cached current user from the server and write the result
 * into the cache, returning the authoritative body.
 *
 * Through `refetchApiQuery`, which is this function's own rule made general:
 * NEVER invalidate to re-read after a write. `invalidateQueries` can be answered
 * by an identical `/users/me` GET that was already in flight when the write was
 * issued, leaving the cache holding pre-write data — and `useCurrentUser` runs a
 * `durable` + `keepPreviousData` query, so such a GET is frequently in flight.
 * Post-write refreshes (pinning a room, editing the profile, finishing signup, …)
 * must take this path or the UI silently keeps stale data. Callers that gate
 * navigation on the profile — `isUserOnboarded` on profile-gated pages such as
 * JuisciGPT, the practice Explore gate — otherwise bounce a user on the state
 * they were in before the write.
 *
 * What the primitive adds over the direct GET this used to do: it CANCELS that
 * in-flight request first, so it cannot land afterwards and overwrite the fresh
 * body, and it writes through `setQueryData`, which also seeds an entry nothing
 * had read yet (`replaceCurrentUserData` no-ops on a cold cache).
 */
export async function reloadCurrentUser(): Promise<CurrentUser | null> {
  // No getFirebaseToken() pre-gate: apiClient attaches auth itself, and the
  // token can transiently read null right after signup. Gating here would
  // silently skip the refresh, leaving the cache pre-write and bouncing the
  // freshly-onboarded user. A genuine no-auth case just 401s into the catch.
  try {
    return await refetchApiQuery(
      "/webapp/private/users/me",
      getCurrentUserQueryInput()
    );
  } catch {
    // Best-effort refresh: on failure the caller keeps the existing cached
    // user. onRequestError already surfaces the backend error to the user.
    return null;
  }
}

export async function ensureCurrentUser(): Promise<CurrentUser | null> {
  const firebaseToken = await getFirebaseToken();
  if (firebaseToken === null) return null;
  const input = getCurrentUserQueryInput();
  const cached = getApiQueryData("/webapp/private/users/me", input);
  if (cached !== null) return cached;
  try {
    const { body } = await apiClient.get("/webapp/private/users/me", input);
    setApiQueryBody("/webapp/private/users/me", input, () => body);
    return body;
  } catch {
    return null;
  }
}
